import {computed} from "vue";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import {mutate} from "swrv";
import {teamPlanningApi} from "@/container";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {TeamMemberSchedule} from "@/core/interfaces/TeamPlanning";

/**
 * The planning profiles and recurring shifts of the current organization,
 * indexed by access point id.
 */
const useTeamSchedule = () => {
    const organizationId = useOrganizationId();

    const key = computed(() => {
        if (!organizationId.value) {
            return null;
        }

        return cacheKey(`organizations/${organizationId.value}/team-schedule`);
    });

    const fetchData = () => {
        if (!organizationId.value) {
            return Promise.resolve([]);
        }

        return teamPlanningApi().schedules(organizationId.value);
    };

    const {data, isLoading, isValidating} = useSwr(
        () => key.value,
        () => fetchData()
    );

    const schedulesByAccessId = computed(() => {
        const map = new Map<number, TeamMemberSchedule>();

        (data.value ?? []).forEach(schedule => map.set(schedule.user_access_point_id, schedule));

        return map;
    });

    /**
     * Write a schedule the API just returned straight into the cache: every
     * save already answers with the fresh row, so no refetch is needed.
     * Without a loaded list to merge into, or with a fetch that started before
     * the save and would overwrite the row, the whole list is fetched again.
     */
    const upsertSchedule = (updated: TeamMemberSchedule) => {
        if (!key.value) {
            return;
        }

        if (!data.value || isValidating.value) {
            mutate(key.value, fetchData());

            return;
        }

        const next = [...(data.value ?? [])];
        const index = next.findIndex(schedule => schedule.user_access_point_id === updated.user_access_point_id);

        if (index >= 0) {
            next[index] = updated;
        } else {
            next.push(updated);
        }

        mutate(key.value, next);
    };

    return {
        data,
        schedulesByAccessId,
        upsertSchedule,
        isLoading,
        organizationId
    };
};

export default useTeamSchedule;
